import React, { useEffect, useState } from 'react';
import {
  Modal, Input, Button, Space, message, DatePicker, Table, Upload, Descriptions,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { parseISO, format } from 'date-fns';
import dayjs from 'dayjs';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';
import {
  formatMaterialIntegerDisplay,
  materialDisplayUnit,
} from '../utils';

const InvoiceCardModal = ({ visible, invoiceId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [name, setName] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [moves, setMoves] = useState([]);

  const loadInvoice = () => {
    if (!invoiceId) return;
    setLoading(true);
    Promise.all([
      Rest.get(`/api/v1/invoices/${invoiceId}.json`),
      Rest.get('/api/v1/material_moves.json', {
        params: { page: 1, per: 500, search: { invoice_id: invoiceId } },
      }),
    ])
      .then(([invoiceResp, movesResp]) => {
        const inv = invoiceResp.data.invoice;
        setInvoice(inv);
        setName(inv.name || '');
        setInvoiceDate(inv.invoice_date ? dayjs(inv.invoice_date) : null);
        setSelectedFile(null);
        setMoves(movesResp.data.material_moves || []);
      })
      .catch(() => message.error('Ошибка загрузки накладной'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible && invoiceId) {
      loadInvoice();
    } else if (!visible) {
      setInvoice(null);
      setSelectedFile(null);
    }
  }, [visible, invoiceId]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      message.error('Введите название накладной');
      return;
    }

    setSaving(true);
    const onError = (err) => {
      const d = err.response?.data;
      const msg = d?.errors
        ? (typeof d.errors === 'object' ? Object.values(d.errors).flat().join(', ') : String(d.errors))
        : 'Ошибка сохранения накладной';
      message.error(msg);
    };
    const onDone = (resp) => {
      const inv = resp.data.invoice;
      setInvoice(inv);
      setName(inv.name || '');
      setInvoiceDate(inv.invoice_date ? dayjs(inv.invoice_date) : null);
      setSelectedFile(null);
      message.success('Накладная сохранена');
      if (onSuccess) onSuccess();
    };

    if (selectedFile) {
      const fd = new FormData();
      fd.append('invoice[name]', trimmedName);
      if (invoiceDate) fd.append('invoice[invoice_date]', invoiceDate.format('YYYY-MM-DD'));
      fd.append('invoice[file]', selectedFile);
      Rest.putForm(`/api/v1/invoices/${invoiceId}.json`, fd)
        .then(onDone)
        .catch(onError)
        .finally(() => setSaving(false));
    } else {
      Rest.put(`/api/v1/invoices/${invoiceId}.json`, {
        invoice: {
          name: trimmedName,
          invoice_date: invoiceDate ? invoiceDate.format('YYYY-MM-DD') : null,
        },
      })
        .then(onDone)
        .catch(onError)
        .finally(() => setSaving(false));
    }
  };

  const moveColumns = [
    {
      title: 'Материал',
      dataIndex: 'material_name',
      ellipsis: true,
    },
    {
      title: 'Кол-во',
      width: 100,
      render: (_, record) => {
        const unit = materialDisplayUnit(record);
        const qty = formatMaterialIntegerDisplay(record.quantity);
        return unit ? `${qty} ${unit}` : qty;
      },
    },
    {
      title: 'Куда',
      dataIndex: 'destination_label',
      ellipsis: true,
    },
  ];

  return (
    <Modal
      title={invoice ? `Накладная: ${invoice.name}` : 'Накладная'}
      open={visible}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={(
        <Space>
          <Button onClick={onClose}>Закрыть</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Сохранить
          </Button>
        </Space>
      )}
    >
      <Preloader loading={loading}>
        {invoice && (
          <>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Склад">
                {invoice.warehouse_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Создана">
                {invoice.created_at
                  ? format(parseISO(invoice.created_at), 'dd.MM.yyyy HH:mm')
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Номер / название</div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Номер или название накладной"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Дата накладной</div>
              <DatePicker
                value={invoiceDate}
                onChange={setInvoiceDate}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                allowClear
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Файл накладной</div>
              {invoice.document_url && !selectedFile && (
                <div style={{ marginBottom: 8 }}>
                  <a href={invoice.document_url} target="_blank" rel="noopener noreferrer">
                    {invoice.document_filename || 'Текущий файл'}
                  </a>
                </div>
              )}
              <Upload
                maxCount={1}
                beforeUpload={(file) => {
                  setSelectedFile(file);
                  return false;
                }}
                onRemove={() => setSelectedFile(null)}
                fileList={selectedFile ? [selectedFile] : []}
              >
                <Button icon={<UploadOutlined />}>
                  {invoice.document_url ? 'Заменить файл' : 'Прикрепить файл'}
                </Button>
              </Upload>
            </div>

            {moves.length > 0 && (
              <>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Позиции поступления ({moves.length})
                </div>
                <Table
                  rowKey="id"
                  size="small"
                  columns={moveColumns}
                  dataSource={moves}
                  pagination={false}
                  scroll={{ y: 240 }}
                />
              </>
            )}
          </>
        )}
      </Preloader>
    </Modal>
  );
};

export default InvoiceCardModal;
